import { useState, useEffect, useMemo } from 'react'
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'
// Use the app's shared client so reads carry the signed-in admin session —
// the SMS tables' RLS policy ("Admins have full access") is authenticated-only,
// so a standalone anon client sees empty result sets, not errors.
import { supabase } from '../../lib/supabase'

const DARK_TOOLTIP = {
  backgroundColor: '#1f2937',
  border: '1px solid #374151',
  borderRadius: '8px',
  color: '#fff'
}

// Collapse an Infobip status — either the coarse group from SmsEventTypes
// (DELIVERED / PENDING / UNDELIVERABLE / EXPIRED / REJECTED) or the raw
// status.name (DELIVERED_TO_HANDSET, PENDING_ENROUTE, REJECTED_PREFIX_MISSING,
// ...) — into the dashboard's four families. Order matters: "UNDELIVERABLE"
// must not match the DELIVER test first.
function statusFamily(value) {
  const s = String(value || '').toUpperCase()
  if (!s || s === 'SENT' || /PENDING|ENROUTE|ACCEPTED/.test(s)) return 'sent'
  if (/REJECT|UNDELIVER|FAIL|ERROR/.test(s)) return 'failed'
  if (/EXPIRED|BOUNCE/.test(s)) return 'bounced'
  if (/DELIVER/.test(s)) return 'delivered'
  return 'other'
}

// Prefer the lookup group (stable), fall back to the raw status string.
function eventFamily(event) {
  const group = event?.SmsEventTypes?.EventDescription
  const family = statusFamily(group)
  return family === 'other' ? statusFamily(event?.Status) : family
}

export default function DashboardTab() {
  const [stats, setStats] = useState({
    totalSent: 0,
    delivered: 0,
    failed: 0,
    pending: 0,
    bounced: 0,
    uniqueRecipients: 0
  })
  const [recentActivity, setRecentActivity] = useState([])
  const [allEntries, setAllEntries] = useState([])
  const [allEvents, setAllEvents] = useState([])
  const [loading, setLoading] = useState(true)

  // Analytics accordion state
  const [analyticsOpen, setAnalyticsOpen] = useState(false)
  const [statusDistOpen, setStatusDistOpen] = useState(false)
  const [hourlyOpen, setHourlyOpen] = useState(false)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      // Get total SMS entries (live schema: ToNumber, not ToMsisdn)
      const { data: entries, error: entriesError } = await supabase
        .from('SmsEntries')
        .select('SmsEntryId, ToNumber, Created')
        .order('Created', { ascending: false })

      if (entriesError) throw entriesError

      setAllEntries(entries || [])
      const totalSent = entries?.length || 0

      // Get delivery events. Status holds the raw Infobip status.name
      // (DELIVERED_TO_HANDSET, PENDING_ENROUTE, ...); the coarse group lives on
      // the SmsEventTypes lookup, so pull it through the FK for classification.
      const { data: events, error: eventsError } = await supabase
        .from('SmsEntryDeliveryEvents')
        .select('SmsEntryDeliveryEventId, Status, Timestamp, SmsEntryId, SmsEventTypes(EventDescription)')

      if (eventsError) throw eventsError

      setAllEvents(events || [])

      // Count events by family
      const eventCounts = {
        delivered: 0,
        failed: 0,
        bounced: 0
      }

      if (events) {
        events.forEach(event => {
          const family = eventFamily(event)

          if (family === 'delivered') eventCounts.delivered++
          else if (family === 'failed') eventCounts.failed++
          else if (family === 'bounced') eventCounts.bounced++
        })
      }

      // Calculate pending: sent but no terminal delivery event yet. An Infobip
      // PENDING_* intermediate report does not resolve the message.
      const resolvedIds = new Set()
      if (events) {
        events.forEach(event => {
          if (eventFamily(event) !== 'sent') resolvedIds.add(event.SmsEntryId)
        })
      }
      const pending = totalSent - resolvedIds.size

      // Count unique recipients
      const uniqueRecipients = new Set(entries?.map(e => e.ToNumber) || []).size

      setStats({
        totalSent,
        ...eventCounts,
        pending,
        uniqueRecipients
      })

      // Get recent activity (last 10 SMS entries with their latest event)
      const recent = entries?.slice(0, 10).map(entry => {
        const entryEvents = events?.filter(e => e.SmsEntryId === entry.SmsEntryId)
        const latestEvent = entryEvents?.sort((a, b) =>
          new Date(b.Timestamp) - new Date(a.Timestamp)
        )[0]

        return {
          ...entry,
          latestStatus: latestEvent?.Status || 'sent',
          latestTimestamp: latestEvent?.Timestamp || entry.Created
        }
      }) || []

      setRecentActivity(recent)

    } catch (error) {
      console.error('Failed to load SMS dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  // --- Computed chart data ---

  const dailyData = useMemo(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 29)
    const dayMap = {}
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().slice(0, 10)
      dayMap[key] = { date: key, sent: 0, delivered: 0 }
    }
    allEntries.forEach(e => {
      const day = new Date(e.Created).toISOString().slice(0, 10)
      if (dayMap[day]) dayMap[day].sent++
    })
    allEvents.forEach(e => {
      if (eventFamily(e) === 'delivered') {
        const day = new Date(e.Timestamp).toISOString().slice(0, 10)
        if (dayMap[day]) dayMap[day].delivered++
      }
    })
    return Object.values(dayMap)
  }, [allEntries, allEvents])

  const statusDistData = useMemo(() => {
    return [
      { name: 'Delivered', value: stats.delivered, color: '#22c55e' },
      { name: 'Failed', value: stats.failed, color: '#ef4444' },
      { name: 'Bounced', value: stats.bounced, color: '#f97316' },
      { name: 'Pending', value: stats.pending, color: '#eab308' },
    ].filter(d => d.value > 0)
  }, [stats])

  const hourlyData = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ hour: `${i}:00`, events: 0 }))
    allEvents.forEach(e => {
      const h = new Date(e.Timestamp).getHours()
      hours[h].events++
    })
    return hours
  }, [allEvents])

  const getPercentage = (value, total) => {
    if (total === 0) return 0
    return Math.round((value / total) * 100)
  }

  const getStatusBadge = (status) => {
    const family = statusFamily(status)
    const raw = String(status || 'sent')

    if (family === 'delivered') {
      return <span title={raw} className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">Delivered</span>
    } else if (family === 'failed') {
      return <span title={raw} className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">Failed</span>
    } else if (family === 'bounced') {
      return <span title={raw} className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 rounded-full">Bounced</span>
    } else if (family === 'sent') {
      return <span title={raw} className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">Sent</span>
    }

    return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded-full capitalize">{raw}</span>
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">SMS Service Dashboard</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of SMS delivery performance</p>
        </div>
        <button
          onClick={loadDashboardData}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid — 6 cards, WhatsApp style (icon right, border, hover:shadow-lg) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Total Sent */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title="Total number of SMS messages sent through the system"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sent</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.totalSent}</p>
            </div>
            <div className="bg-blue-500 p-3 rounded-lg text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Delivered */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title="SMS messages successfully delivered to recipients. Calculated as (delivered / total sent) x 100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Delivered</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.delivered}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(stats.delivered, stats.totalSent)}% delivery rate
              </p>
            </div>
            <div className="bg-green-500 p-3 rounded-lg text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Failed */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title="SMS messages that failed, were rejected, or were undeliverable. Calculated as (failed / total sent) x 100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Failed</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.failed}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(stats.failed, stats.totalSent)}% failure rate
              </p>
            </div>
            <div className="bg-red-500 p-3 rounded-lg text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Pending */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title="SMS messages sent but not yet confirmed as delivered or failed"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.pending}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Awaiting delivery confirmation
              </p>
            </div>
            <div className="bg-yellow-500 p-3 rounded-lg text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Bounced */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title="SMS messages that bounced or expired. Calculated as (bounced / total sent) x 100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Bounced</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.bounced}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(stats.bounced, stats.totalSent)}% bounce rate
              </p>
            </div>
            <div className="bg-orange-500 p-3 rounded-lg text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Unique Recipients */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title="Count of distinct phone numbers (ToNumber) that received at least one SMS"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unique Recipients</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.uniqueRecipients}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Distinct phone numbers
              </p>
            </div>
            <div className="bg-teal-500 p-3 rounded-lg text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Analytics Accordion */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setAnalyticsOpen(!analyticsOpen)}
          className="w-full px-6 py-4 flex items-center justify-between text-left border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        >
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Analytics</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">30-day trends and distribution insights</p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${analyticsOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {analyticsOpen && (
          <div className="p-6 space-y-4">
            {/* Daily Volume Chart - Always visible when analytics open */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">Daily SMS Volume</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Track your sending patterns and delivery over the last 30 days</p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyData}>
                    <defs>
                      <linearGradient id="smsSent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="smsDelivered" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      tickLine={{ stroke: '#374151' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      tickLine={{ stroke: '#374151' }}
                    />
                    <Tooltip contentStyle={DARK_TOOLTIP} />
                    <Legend />
                    <Area type="monotone" dataKey="sent" name="Sent" stroke="#3b82f6" fillOpacity={1} fill="url(#smsSent)" />
                    <Area type="monotone" dataKey="delivered" name="Delivered" stroke="#22c55e" fillOpacity={1} fill="url(#smsDelivered)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Status Distribution Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setStatusDistOpen(!statusDistOpen)}
                className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900 rounded">
                    <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Status Distribution</span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${statusDistOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {statusDistOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Overall breakdown of SMS delivery outcomes</p>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusDistData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={{ stroke: '#9ca3af' }}
                        >
                          {statusDistData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={DARK_TOOLTIP} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Hourly Activity Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setHourlyOpen(!hourlyOpen)}
                className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-purple-100 dark:bg-purple-900 rounded">
                    <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Hourly Activity</span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${hourlyOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {hourlyOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">SMS event distribution by hour of day</p>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                        <XAxis
                          dataKey="hour"
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          interval="preserveStartEnd"
                        />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                        <Tooltip contentStyle={DARK_TOOLTIP} />
                        <Legend />
                        <Bar dataKey="events" name="Events" fill="#a855f7" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Last 10 SMS messages sent</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Recipient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity) => (
                  <tr key={activity.SmsEntryId} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {activity.ToNumber || <span className="text-gray-400 italic">Unknown</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(activity.latestStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(activity.latestTimestamp).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No recent activity
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
